import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideEnvironmentInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withRouterConfig } from '@angular/router';

import { routes } from './app.routes';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { TokenInterceptor } from './interceptors/token-interceptor';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';
import { SessionCountdownService } from './core/auth/services/session-countdown.service';
import { provideIconConfig } from './core/icon-svg/icon-svg.module';
import * as Sentry from "@sentry/angular";

// Locale files available under src/assets/i18n (excludes non-locale files like en-pii.json).
export const SUPPORTED_LANGS = new Set([
  'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'it',
  'ja', 'nl', 'no', 'pl', 'pt', 'ru', 'se', 'sv', 'zh',
]);

export function getInitialLang(): string {
  const browserLang = (window.navigator?.language ?? 'en').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.has(browserLang) ? browserLang : 'en';
}

// `defaultLanguage` alone is only a missing-key fallback; `use()` is what loads translations.
export function initializeTranslations(translate: TranslateService): Promise<unknown> {
  return firstValueFrom(translate.use(getInitialLang()));
}

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(async () => inject(Sentry.TraceService)),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withRouterConfig({ onSameUrlNavigation: 'reload' })),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    provideTranslateService({
      defaultLanguage: 'en',
    }),
    provideTranslateHttpLoader({
      prefix: './assets/i18n/',
      suffix: '.json',
    }),
    // `defaultLanguage` alone is only a missing-key fallback; `use()` is what loads translations.
    provideAppInitializer(() => initializeTranslations(inject(TranslateService))),
    provideEnvironmentInitializer(() => inject(SessionCountdownService)),
    provideIconConfig(),
  ],
};
